import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { type PaginatedResponse } from "@/shared/http";
import type { SearchQueryBody } from "@/types";

import { type InvocationRecord } from "../ActionRecord";

export interface UseInvocationSearchOptions {
  /**
   * What is being asked for, by value: the filters as the reader set them, the sort, the page
   * size and the continuation token. Serializable; two renders that ask the same thing share one
   * query.
   */
  key: unknown;
  /**
   * Builds the whole POST body (condition, sort, page size, token) when a fetch starts. The hook
   * adds nothing. Built per fetch rather than taken by value so a relative date filter, which
   * expands to bounds that follow the clock, keeps one identity: a refresh is then a refetch that
   * holds the rows on screen, not a new query that replaces them.
   */
  body: () => SearchQueryBody;
  refetchInterval?: number | false;
}

/**
 * One page of the current org's invocations. A changed filter, sort, page size or token is a new
 * query, with the previous page standing in until it lands; a refresh of the same key is not.
 */
export function useInvocationSearch({
  key,
  body,
  refetchInterval = false,
}: UseInvocationSearchOptions): UseQueryResult<
  PaginatedResponse<InvocationRecord>,
  Error
> {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useQuery<PaginatedResponse<InvocationRecord>, Error>({
    queryKey: ["invocationSearch", currentOrganization?.org_id, key],
    queryFn: ({ signal }) => {
      if (currentOrganization === null) {
        throw new Error("Requires login");
      }
      return actionService.queryInvocations(body(), {
        orgId: currentOrganization.org_id,
        abortSignal: signal,
      });
    },
    enabled: currentOrganization !== null,
    refetchInterval,
    staleTime: 15_000,
    // A page change or refresh keeps the previous rows on screen instead of blanking the grid.
    placeholderData: keepPreviousData,
  });
}
